import { inject, Injectable, signal, Signal, WritableSignal } from '@angular/core';
import { MenuAction } from '@shared/interfaces/header/menu-action';
import { ActivationStart, NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';

/**
 * A service to manage the navigation header, including dynamic menus and page titles.
 * Automatically clears the menu and title on route changes.
 *
 *
 * ```typescript
 * this.headerService.set(
 *   [
 *     { id: 'action1', title: 'Action 1' },
 *     { id: 'action2', title: 'Action 2' }
 *   ],
 *   {
 *     action1: () => console.log('Custom Callback for Action 1')
 *   },
 *   {
 *     action2: () => true // Disable Action 2
 *   }
 * );
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class HeaderService {
  private readonly router = inject(Router);

  /**
   * Signal for managing the current state of header menu actions.
   * @private
   */
  private readonly menuState: WritableSignal<MenuAction[]> = signal([]);

  /**
   * Read-only signal providing access to the current menu actions.
   */
  public menu: Signal<MenuAction[]> = this.menuState.asReadonly();

  /**
   * Signal for managing the current page title in the header.
   * @private
   */
  private readonly titleState: WritableSignal<string> = signal('');

  /**
   * Read-only signal providing access to the current page title.
   */
  public title: Signal<string> = this.titleState.asReadonly();

  /**
   * Signal for managing the current ressource in the header.
   * @private
   */
  private readonly ressourceState: WritableSignal<string> = signal('');

  /**
   * Read-only signal providing access to the current ressource.
   */
  public ressource: Signal<string> = this.ressourceState.asReadonly();

  /**
   * Signal tracking whether the page-level action menu is open.
   * Components that interact with the menu (e.g. tables doing keyboard
   * navigation that need to yield while the menu is open and refocus
   * when it closes) can subscribe to this.
   * @private
   */
  private readonly actionsOpenState: WritableSignal<boolean> = signal(false);

  /**
   * Read-only signal indicating whether the page-level action menu is open.
   */
  public actionsOpen: Signal<boolean> = this.actionsOpenState.asReadonly();

  /**
   * Updates the menu-open state. Called by HeaderComponent whenever the
   * local menu visibility changes.
   */
  public setActionsOpen(open: boolean): void {
    this.actionsOpenState.set(open);
  }

  /**
   * Stores the route path where the resource was set (e.g., '/meteringpoint/:id')
   * @private
   */
  private ressourceRouteContext: string = '';

  /**
   * Initializes the `HeaderService` and sets up automatic menu clearing on route changes.
   */
  constructor() {
    /**
     * Clears the menu actions and title whenever a route activation starts.
     * Also clears the resource if navigating outside its route context.
     */
    this.router.events
      .pipe(filter(event => event instanceof ActivationStart))
      .subscribe(() => {
        this.clear();
        this.clearResourceIfNeeded();
      });

    /**
     * Additional check on NavigationEnd to catch routes that skip ActivationStart
     */
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        this.clearResourceIfNeeded();
      });
  }

  /**
   * Updates the header menu with the specified actions along with optional callback and disabled mappings.
   *
   * @param menu - An array of `MenuAction` objects representing the new menu actions.
   * @param callbackMap - An optional mapping of action IDs to corresponding callback functions.
   * @param disabledMap - An optional mapping of action IDs to boolean values or functions that determine if the action is disabled.
   *
   */
  public setActions(
    menu: MenuAction[],
    callbackMap?: Record<string, () => void>,
    disabledMap?: Record<string, () => boolean>
  ): void {
    const enhancedMenu = menu.map(action => ({
      ...action,
      disabled: disabledMap?.[action.id] ?? action.disabled,
      callback: callbackMap?.[action.id] ?? action.callback,
    }));

    this.menuState.set(enhancedMenu);
  }

  /**
   * Sets the title in the navigation header.
   *
   * @param title - The new title to be displayed in the header.
   */
  public setTitle(title?: string): void {
    if (!title) return;
    this.titleState.set(title);
  }

  /**
   * Sets the resource in the header and stores the route context.
   *
   * @param resource - The resource identifier to display.
   */
  public setResource(resource?: string): void {
    if (!resource) return;
    this.ressourceState.set(resource);
    this.ressourceRouteContext = this.extractRouteContext(this.router.url);
  }

  /**
   * Extracts the route context from a URL (e.g., '/meteringpoint/123' from '/meteringpoint/123/details').
   * This represents the parent route pattern with its ID.
   *
   * @param url - The full URL path
   * @returns The route context (e.g., '/meteringpoint/123'), or empty string if no ID present
   * @private
   */
  private extractRouteContext(url: string): string {
    const cleanUrl = url.split('?')[0].split('#')[0];

    const match = /^(\/[^/]+\/[^/]+)/.exec(cleanUrl);
    return match ? match[1] : '';
  }

  /**
   * Checks if the current URL is a child route of the stored resource route context.
   *
   * @private
   */
  private clearResourceIfNeeded(): void {
    if (!this.ressourceState()) return;

    if (!this.ressourceRouteContext) {
      this.clearRessource();
      return;
    }

    const currentUrl = this.router.url.split('?')[0].split('#')[0];
    const currentContext = this.extractRouteContext(currentUrl);

    if (!currentContext || currentContext !== this.ressourceRouteContext) {
      this.clearRessource();
    }
  }

  /**
   * Clears all menu actions and resets the title in the header.
   */
  public clear(): void {
    this.menuState.set([]);
    this.titleState.set('');
  }

  /**
   * Clears the resource and its route context.
   */
  public clearRessource(): void {
    this.ressourceState.set('');
    this.ressourceRouteContext = '';
  }
}
